import { useState } from "react";
import { money } from "@/lib/api";
import { Sparkles, Check, X, BadgeCheck } from "lucide-react";

// Renter view: homes a landlord suggested (pre-qualified, no re-application needed).
export const SuggestedForYou = ({ suggestions = [], onRespond }) => {
  const open = suggestions.filter((s) => s.status === "sent");
  if (open.length === 0) return null;

  return (
    <div data-testid="suggested-for-you" className="mt-6 space-y-4">
      {open.map((s) => (
        <SuggestionCard key={s.id} s={s} onRespond={onRespond} />
      ))}
    </div>
  );
};

const SuggestionCard = ({ s, onRespond }) => {
  const props = (s.properties || []).filter((p) => p.available);
  const [pick, setPick] = useState(props.length === 1 ? props[0].id : null);

  return (
    <div data-testid={`suggestion-${s.id}`} className="bg-white border border-forest/30 rounded-2xl p-5">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-forest/10 flex items-center justify-center shrink-0"><Sparkles className="h-5 w-5 text-forest" /></div>
        <div className="min-w-0">
          <p className="font-heading text-lg text-ink">{s.landlord_name} suggested a home for you</p>
          <p className="text-sm text-mute mt-0.5 inline-flex items-center gap-1.5">
            <BadgeCheck className="h-4 w-4 text-forest" /> You're already pre-qualified — no new application needed.
          </p>
          {s.note && <p className="text-sm text-ink mt-2 italic bg-bone rounded-lg px-3 py-2">"{s.note}"</p>}
        </div>
      </div>

      {props.length === 0 ? (
        <p className="text-sm text-mute mt-4">These homes are no longer available.</p>
      ) : (
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          {props.map((p) => {
            const on = pick === p.id;
            return (
              <button
                key={p.id} type="button" data-testid={`suggestion-prop-${p.id}`}
                onClick={() => setPick(p.id)}
                className={`text-left flex gap-3 rounded-xl border p-2.5 transition-colors ${on ? "border-forest bg-forest/[0.04] ring-1 ring-forest/30" : "border-line hover:bg-bone"}`}
              >
                <img src={p.image || "https://images.unsplash.com/photo-1568605114967-8130f3a36994?q=80&w=240"} alt="" className="h-16 w-20 object-cover rounded-lg shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-ink truncate">{p.title}</p>
                  <p className="text-xs text-mute truncate">{p.city}, {p.state}</p>
                  <p className="text-sm font-semibold text-forest mt-0.5">{money(p.monthly_rent)}/mo</p>
                </div>
                <span className={`h-5 w-5 rounded-full border flex items-center justify-center shrink-0 self-center ${on ? "bg-forest border-forest text-white" : "border-line"}`}>{on && <Check className="h-3.5 w-3.5" />}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          data-testid={`suggestion-interested-${s.id}`}
          disabled={!pick}
          onClick={() => onRespond(s.id, true, pick)}
          className="bg-forest text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-forest-hover flex items-center gap-1.5 disabled:opacity-50"
        >
          <Check className="h-4 w-4" /> I'm interested{props.length > 1 && !pick ? " (pick one)" : ""}
        </button>
        <button
          data-testid={`suggestion-decline-${s.id}`}
          onClick={() => onRespond(s.id, false)}
          className="border border-line text-ink px-4 py-2 rounded-lg text-sm hover:bg-bone flex items-center gap-1.5"
        >
          <X className="h-4 w-4" /> No thanks
        </button>
      </div>
    </div>
  );
};
